import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AlertTriangle, Trash2 } from 'lucide-react'
import { useAuthStore } from '@/application/stores/auth.store'
import { useGetMe, useDeleteMe } from '@/application/services/user.service'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/forms/Input'

/**
 * Danger zone section: account-deletion confirmation flow that requires the
 * user to retype their username. On success it logs the user out and routes
 * back to the home page. Owns its own confirmation toggle and input state.
 */
export function DangerZoneSection() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { data: profile } = useGetMe()
  const { mutateAsync: deleteMe, isPending: isDeleting } = useDeleteMe()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')

  const expectedUsername = profile?.username ?? user?.username ?? ''

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-destructive" />
          <CardTitle className="text-destructive">{t('admin.profile_delete_title')}</CardTitle>
        </div>
        <CardDescription>{t('admin.profile_delete_desc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2 p-3 bg-destructive/5 border border-destructive/20 rounded-md text-sm text-muted-foreground">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <p>{t('admin.profile_delete_warning')}</p>
        </div>

        {!showDeleteConfirm ? (
          <Button
            variant="destructive"
            onClick={() => {
              setShowDeleteConfirm(true)
              setDeleteConfirmInput('')
            }}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t('admin.profile_delete_btn')}
          </Button>
        ) : (
          <div className="space-y-3 p-4 border border-destructive/30 rounded-md bg-destructive/5">
            <p className="text-sm font-medium">{t('admin.profile_delete_confirm_label')}</p>
            <Input
              value={deleteConfirmInput}
              onChange={(event) => setDeleteConfirmInput(event.target.value)}
              placeholder={expectedUsername}
              autoComplete="off"
              autoFocus
            />
            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setDeleteConfirmInput('')
                }}
              >
                {t('admin.profile_delete_cancel')}
              </Button>
              <Button
                variant="destructive"
                isLoading={isDeleting}
                disabled={deleteConfirmInput !== expectedUsername}
                onClick={async () => {
                  try {
                    await deleteMe()
                    toast.success(t('admin.profile_delete_success'))
                    logout()
                    navigate({ to: '/' })
                  } catch {
                    toast.error(t('admin.profile_delete_error'))
                  }
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('admin.profile_delete_confirm_btn')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
